import { useTranslation } from "react-i18next";

import {
  CardNote,
  MinBetPill,
} from "@/components/campaigns/campaign-shell/campaign-sections";
import { TaskIcon } from "@/components/campaigns/task-icon";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { TaskSchema } from "@/types";

/**
 * Sidebar "How to get points" rules card — one row per point-scoring task, in the same anatomy
 * as a mission task row: a round chip with the task's glyph, the title, and the value as a
 * text Badge at the far end (tabular, right-aligned, so "+1 pt" and "+50 pts" line up). Rows
 * run edge to edge under the title, divided by hairlines inset to the content.
 * Data is `tournament.tasks` (title + points + kind). Figma 5129:6786. A race adds its spin cap
 * (`campaign.max_spins`) as a footnote under the rules, divided by the same hairline.
 */
export const HowToGetPointsCard = ({
  tasks,
  minimumBet,
  maxSpins,
}: {
  tasks: TaskSchema[];
  minimumBet?: { value: number; currency: string; display?: string };
  /** A race's cap on counted spins; null/absent for every other subtype. */
  maxSpins?: number | null;
}) => {
  const { t } = useTranslation();
  return (
    <Card
      inset="none"
      title={t("tournament.howToGetPoints", "How to get points")}
      aside={<MinBetPill minimumBet={minimumBet} />}
      titleRowClassName="pb-1"
    >
      <div className="px-(--card-pad) pb-1 lg:pb-2">
        <PointsRuleList tasks={tasks} />
        {maxSpins ? (
          <CardNote className="border-t border-foreground/[0.06] py-2 lg:py-2.5">
            {t(
              "tournament.spinCapRule",
              "Only your first {{count}} qualifying spins count",
              { count: maxSpins }
            )}
          </CardNote>
        ) : null}
      </div>
    </Card>
  );
};

/** The point rules as rows — the card's body, and the T&C's scoring table. */
export const PointsRuleList = ({
  tasks,
  className,
}: {
  tasks: TaskSchema[];
  className?: string;
}) => {
  const { t } = useTranslation();
  return (
    <ul className={cn("flex flex-col", className)}>
      {tasks.map((task, index) => (
        <li
          key={`${task.order}-${index}`}
          className={cn(
            "flex w-full items-center gap-2.5 py-2 lg:py-2.5",
            index > 0 && "border-t border-foreground/[0.06]"
          )}
        >
          <Badge
            variant="ghost"
            color="neutral"
            icon={<TaskIcon kind={task.kind} size="xs" />}
            className="text-[12px] lg:text-[14px]"
          />
          <span className="min-w-0 flex-1 truncate font-body text-[12px] leading-4 text-foreground lg:text-[14px] lg:leading-5">
            {task.title}
          </span>
          <Badge
            variant="text"
            color="primary"
            className="min-w-14 shrink-0 justify-end text-[12px] lg:text-[13px]"
          >
            {t("tournament.pointsPill", "+{{count}} pts", {
              count: task.points ?? 0,
            })}
          </Badge>
        </li>
      ))}
    </ul>
  );
};
